/**
 * The institution picker (TMMAPP-0163 item 11; TMMAPI-0195 U2, MM-D-152): an account's bank, picked
 * from the catalog and searched in the member's country first. It replaces the free-text field in
 * the account form and the add-account form.
 *
 * - Searches from 2 characters, 250 ms after typing stops: `GET /v1/institutions/search` with
 *   `scope=LOCAL` and the member's served country (`/me.user.country`, never an account's currency).
 *   A name, a 9-digit routing number or a BIC all search the same way.
 * - Each option is the name, then "City, Region", and the country when it isn't the member's. A
 *   routing number or a BIC is never shown (the search answers neither).
 * - An answer with `widened: true` (nothing in the member's country) lists its options under
 *   "Other countries". "Search all countries" re-searches with `scope=ALL`.
 * - "My bank isn't listed" is the last option: it keeps the typed text as the free-text
 *   `institution` (tmm-api files the request), shown with "Not in our list yet". Leaving the field
 *   with typed text that wasn't picked keeps it the same way, so nothing typed is lost.
 * - The result count is announced in a polite live region.
 */
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { Autocomplete, Box, ListSubheader, TextField, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import {
  INSTITUTION_QUERY_MIN,
  institutionsApi,
  type InstitutionOption,
  type InstitutionScope,
} from '@/api/institutions';
import { countryName } from '@/i18n/countries';
import { useLocale } from '@/i18n/useLocale';
import { useOptionalMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';

const SEARCH_DEBOUNCE_MS = 250;

/**
 * The field's value: a catalog institution (`id` set, `name` its catalog name), a typed name that
 * isn't in the catalog (`id` null), or nothing (`id` null, `name` '').
 */
export interface InstitutionValue {
  id: number | null;
  name: string;
}

const NO_INSTITUTION: InstitutionValue = { id: null, name: '' };

type Option =
  | { kind: 'institution'; item: InstitutionOption; other: boolean }
  | { kind: 'all' }
  | { kind: 'unlisted'; typed: string };

/** The answer for one query and scope; `items` null when the search failed. */
interface Results {
  key: string;
  items: InstitutionOption[] | null;
  widened: boolean;
}

const OTHER_GROUP = 'other';

/** True when the combobox input points at a highlighted option. */
function hasActiveOption(target: EventTarget): boolean {
  return (
    target instanceof HTMLElement && (target.getAttribute('aria-activedescendant') ?? '') !== ''
  );
}

interface InstitutionPickerProps {
  value: InstitutionValue;
  onChange: (next: InstitutionValue) => void;
  label: string;
  placeholder?: string | undefined;
  size?: 'small' | 'medium';
  id?: string | undefined;
}

export function InstitutionPicker({
  value,
  onChange,
  label,
  placeholder,
  size = 'medium',
  id,
}: InstitutionPickerProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const country = useOptionalMe()?.user.country ?? null;
  const groupId = useId();
  const [input, setInput] = useState(value.name);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<InstitutionScope>('LOCAL');
  const [results, setResults] = useState<Results | null>(null);
  // "Search all countries" is an option: choosing it re-searches and keeps the list open.
  const keepOpen = useRef(false);

  const query = input.trim();
  const searchable = query.length >= INSTITUTION_QUERY_MIN;
  const key = `${scope}|${country ?? ''}|${query}`;
  useEffect(() => {
    if (!open || !searchable) return undefined;
    let live = true;
    const handle = window.setTimeout(() => {
      institutionsApi.search(query, { scope, country }).then(
        (answer) => {
          if (live) setResults({ key, items: answer.items, widened: answer.widened });
        },
        () => {
          if (live) setResults({ key, items: null, widened: false });
        },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      live = false;
      window.clearTimeout(handle);
    };
  }, [open, searchable, query, scope, country, key]);

  const current = results !== null && results.key === key ? results : null;
  const loading = open && searchable && current === null;
  const options: Option[] =
    current === null
      ? []
      : [
          ...(current.items ?? []).map((item): Option => ({
            kind: 'institution',
            item,
            other: current.widened,
          })),
          ...(scope === 'LOCAL' ? [{ kind: 'all' } as const] : []),
          { kind: 'unlisted', typed: query },
        ];

  const announcement =
    !open || current === null
      ? ''
      : current.items === null
        ? t('institutions.searchFailed')
        : current.widened
          ? t('institutions.resultCountOther', { count: current.items.length })
          : t('institutions.resultCount', { count: current.items.length });

  const notListed = value.id === null && value.name !== '' && query === value.name;

  function commitTyped() {
    if (query === value.name) {
      setInput(value.name);
      return;
    }
    onChange(query === '' ? NO_INSTITUTION : { id: null, name: query });
    setInput(query);
  }

  function place(item: InstitutionOption): string {
    const where = [item.city, item.region].filter((part) => part !== null).join(', ');
    const foreign = item.country !== '' && item.country !== country;
    return [where, foreign ? countryName(item.country, locale) : '']
      .filter((part) => part.length > 0)
      .join(' · ');
  }

  return (
    <>
      <Autocomplete<Option, false, false, false>
        {...(id !== undefined ? { id } : {})}
        size={size}
        fullWidth
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          if (keepOpen.current) {
            keepOpen.current = false;
            return;
          }
          setOpen(false);
        }}
        options={options}
        // The server searched; never re-filter by the typed text.
        filterOptions={(list) => list}
        value={null}
        inputValue={input}
        clearOnBlur={false}
        loading={loading}
        loadingText={t('institutions.searching')}
        noOptionsText={searchable ? t('institutions.searching') : t('institutions.typeMore')}
        getOptionLabel={(option) =>
          option.kind === 'institution'
            ? option.item.name
            : option.kind === 'all'
              ? t('institutions.searchAll')
              : t('institutions.unlisted')
        }
        getOptionKey={(option) =>
          option.kind === 'institution' ? `institution:${option.item.id}` : option.kind
        }
        isOptionEqualToValue={(option, other) =>
          option.kind === other.kind &&
          (option.kind !== 'institution' ||
            (other.kind === 'institution' && option.item.id === other.item.id))
        }
        groupBy={(option) => (option.kind === 'institution' && option.other ? OTHER_GROUP : '')}
        renderGroup={(params) =>
          params.group === OTHER_GROUP ? (
            <li key={params.key} role="presentation">
              <ListSubheader id={groupId} component="div" role="presentation" sx={{ top: -8 }}>
                {t('institutions.otherCountries')}
              </ListSubheader>
              <Box component="ul" role="group" aria-labelledby={groupId} sx={{ p: 0 }}>
                {params.children}
              </Box>
            </li>
          ) : (
            <Fragment key={params.key}>{params.children}</Fragment>
          )
        }
        renderOption={(props, option) => {
          const { key: optionKey, ...rest } = props;
          if (option.kind === 'institution') {
            const where = place(option.item);
            return (
              <li key={optionKey} {...rest}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" component="div">
                    {option.item.name}
                  </Typography>
                  {where.length > 0 && (
                    <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                      {where}
                    </Typography>
                  )}
                </Box>
              </li>
            );
          }
          if (option.kind === 'all') {
            return (
              <li key={optionKey} {...rest} data-institution-search-all="">
                <Typography variant="body2" component="span" sx={{ color: tokens.link }}>
                  {t('institutions.searchAll')}
                </Typography>
              </li>
            );
          }
          return (
            <li key={optionKey} {...rest} data-institution-unlisted="">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" component="div">
                  {t('institutions.unlisted')}
                </Typography>
                <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                  {t('institutions.unlistedKeep', { name: option.typed })}
                </Typography>
              </Box>
            </li>
          );
        }}
        onInputChange={(_event, next, reason) => {
          // A pick sets the text itself (below); MUI's own reset to an option's label is ignored.
          if (reason !== 'input' && reason !== 'clear') return;
          setInput(next);
          if (reason === 'clear') onChange(NO_INSTITUTION);
        }}
        onChange={(_event, option) => {
          if (option === null) return;
          if (option.kind === 'all') {
            // The close that follows a selection, in the same event, is skipped; never a later one.
            keepOpen.current = true;
            window.setTimeout(() => {
              keepOpen.current = false;
            }, 0);
            setScope('ALL');
            return;
          }
          if (option.kind === 'institution') {
            onChange({ id: option.item.id, name: option.item.name });
            setInput(option.item.name);
            return;
          }
          onChange({ id: null, name: option.typed });
          setInput(option.typed);
        }}
        onBlur={commitTyped}
        onKeyDown={(event) => {
          // Enter with nothing highlighted is the form's implicit submit, with no blur first: commit the
          // typed text now, so the submit sees it (React flushes this discrete update before it).
          // "Highlighted" is the input's live aria-activedescendant, which MUI keeps in step with the
          // option Enter would pick, resets included (new input, emptied list, reopen); its
          // onHighlightChange isn't called for those resets (TMMAPP-0165 item 16).
          if (event.key === 'Enter' && (!open || !hasActiveOption(event.target))) commitTyped();
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            helperText={notListed ? t('institutions.notListed') : t('institutions.hint')}
          />
        )}
      />
      <Box component="span" role="status" sx={visuallyHidden}>
        {announcement}
      </Box>
    </>
  );
}
